import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from '../components/ui/dialog';
import {
  Plus, Pencil, Trash2, LogOut, Briefcase, CheckCircle, XCircle
} from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const DEPARTMENTS = [
  'E-Commerce', 'E-Commerce Capital Club', 'Partnerships',
  'Sendex Fulfillment', 'SaaS', 'Brand Incubator',
];
const TYPES = ['Full-time', 'Part-time', 'Contract', 'Internship'];

const emptyForm = {
  title: '', department: 'E-Commerce', location: '',
  employment_type: 'Full-time', description: '', responsibilities: '',
  requirements: '', apply_url: '', is_active: true,
};

const AdminDashboard = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [form, setForm] = useState({ ...emptyForm });
  const [saving, setSaving] = useState(false);

  const getHeaders = useCallback(() => {
    const token = localStorage.getItem('bcg_token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/jobs`, { headers: getHeaders(), withCredentials: true });
      const data = res.data;
      // Support both combined response { jobs, stats } and legacy array response
      if (data.jobs && data.stats) {
        setJobs(data.jobs);
        setStats(data.stats);
      } else if (Array.isArray(data)) {
        setJobs(data);
        const active = data.filter(j => j.is_active).length;
        setStats({ total: data.length, active, inactive: data.length - active });
      }
    } catch (err) {
      console.error('Failed to fetch data:', err);
    }
    setLoading(false);
  }, [getHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openCreate = () => {
    setEditingJob(null);
    setForm({ ...emptyForm });
    setShowDialog(true);
  };

  const openEdit = (job) => {
    setEditingJob(job);
    setForm({
      title: job.title, department: job.department, location: job.location,
      employment_type: job.employment_type, description: job.description,
      responsibilities: job.responsibilities || '',
      requirements: job.requirements, apply_url: job.apply_url || '', is_active: job.is_active,
    });
    setShowDialog(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (editingJob) {
        const { data: updated } = await axios.put(`${API_URL}/api/admin/jobs/${editingJob.id}`, form, {
          headers: getHeaders(), withCredentials: true,
        });
        // Optimistic: update local state immediately
        setJobs(prev => prev.map(j => j.id === editingJob.id ? updated : j));
      } else {
        const { data: created } = await axios.post(`${API_URL}/api/admin/jobs`, form, {
          headers: getHeaders(), withCredentials: true,
        });
        setJobs(prev => [created, ...prev]);
      }
      setShowDialog(false);
      // Recalculate stats from local state
      setJobs(prev => {
        const active = prev.filter(j => j.is_active).length;
        setStats({ total: prev.length, active, inactive: prev.length - active });
        return prev;
      });
    } catch (err) {
      console.error('Save failed:', err);
    }
    setSaving(false);
  };

  const handleToggle = async (jobId) => {
    // Optimistic: toggle immediately in UI
    setJobs(prev => {
      const updated = prev.map(j => j.id === jobId ? { ...j, is_active: !j.is_active } : j);
      const active = updated.filter(j => j.is_active).length;
      setStats({ total: updated.length, active, inactive: updated.length - active });
      return updated;
    });
    try {
      await axios.patch(`${API_URL}/api/admin/jobs/${jobId}/toggle`, {}, {
        headers: getHeaders(), withCredentials: true,
      });
    } catch (err) {
      console.error('Toggle failed:', err);
      fetchData(); // Revert on error
    }
  };

  const handleDelete = async (jobId) => {
    if (!window.confirm('Delete this job posting?')) return;
    // Optimistic: remove immediately from UI
    const previous = jobs;
    setJobs(prev => {
      const updated = prev.filter(j => j.id !== jobId);
      const active = updated.filter(j => j.is_active).length;
      setStats({ total: updated.length, active, inactive: updated.length - active });
      return updated;
    });
    try {
      await axios.delete(`${API_URL}/api/admin/jobs/${jobId}`, {
        headers: getHeaders(), withCredentials: true,
      });
    } catch (err) {
      console.error('Delete failed:', err);
      setJobs(previous); // Revert on error
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const selectStyle = {
    width: '100%',
    height: '36px',
    padding: '0 12px',
    background: 'rgba(255,255,255,0.05)',
    border: '1px solid rgba(255,255,255,0.1)',
    color: '#fff',
    fontFamily: "'Space Grotesk', sans-serif",
    fontSize: '14px',
    borderRadius: '6px',
    outline: 'none',
  };

  return (
    <div
      className="min-h-screen"
      style={{ background: '#0a0a0a', fontFamily: "'Space Grotesk', sans-serif" }}
      data-testid="admin-dashboard"
    >
      {/* Top Bar */}
      <header
        className="flex items-center justify-between px-8 py-4"
        style={{ borderBottom: '1px solid rgba(255,255,255,0.1)' }}
        data-testid="admin-header"
      >
        <div className="flex items-center gap-6">
          <Link
            to="/"
            className="text-white font-bold text-lg no-underline"
            style={{ letterSpacing: '0.15em' }}
          >
            BCG
          </Link>
          <span style={{ color: 'rgba(255,255,255,0.3)' }}>|</span>
          <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px', letterSpacing: '0.1em' }}>
            Admin Dashboard
          </span>
        </div>
        <div className="flex items-center gap-4">
          <span style={{ color: 'rgba(255,255,255,0.4)', fontSize: '14px' }}>
            {user?.email}
          </span>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-white/50 hover:text-white hover:bg-white/5"
            data-testid="logout-btn"
          >
            <LogOut size={16} className="mr-2" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-8 py-10">
        {/* Stats */}
        <div className="grid grid-cols-3 gap-4 mb-10" data-testid="stats-row">
          {[
            { label: 'Total Jobs', value: stats.total, icon: <Briefcase size={20} /> },
            { label: 'Active', value: stats.active, icon: <CheckCircle size={20} /> },
            { label: 'Inactive', value: stats.inactive, icon: <XCircle size={20} /> },
          ].map(s => (
            <div
              key={s.label}
              className="p-6"
              style={{
                background: 'rgba(255,255,255,0.02)',
                border: '1px solid rgba(255,255,255,0.08)',
              }}
              data-testid={`stat-${s.label.toLowerCase().replace(' ', '-')}`}
            >
              <div className="flex items-center gap-3 mb-3" style={{ color: 'rgba(255,255,255,0.4)' }}>
                {s.icon}
                <span className="text-xs uppercase" style={{ letterSpacing: '0.1em' }}>{s.label}</span>
              </div>
              <div className="text-3xl font-bold text-white">{s.value}</div>
            </div>
          ))}
        </div>

        {/* Actions */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-white" style={{ letterSpacing: '-0.01em' }}>
            Job Postings
          </h2>
          <Button
            onClick={openCreate}
            className="bg-white text-black hover:bg-white/90 font-semibold"
            data-testid="add-job-btn"
          >
            <Plus size={16} className="mr-2" /> Add New Job
          </Button>
        </div>

        {/* Job Table */}
        {loading ? (
          <div className="text-center py-16" style={{ color: 'rgba(255,255,255,0.4)' }} data-testid="loading">
            Loading...
          </div>
        ) : jobs.length === 0 ? (
          <div className="text-center py-16" style={{ color: 'rgba(255,255,255,0.4)' }} data-testid="empty-state">
            No job postings yet. Create your first one.
          </div>
        ) : (
          <div
            className="overflow-x-auto"
            style={{ border: '1px solid rgba(255,255,255,0.08)' }}
            data-testid="jobs-table"
          >
            <table className="w-full" style={{ borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  {['Title', 'Department', 'Location', 'Type', 'Posted by', 'Status', 'Actions'].map(h => (
                    <th
                      key={h}
                      className="text-left px-5 py-3 text-xs uppercase font-semibold"
                      style={{ color: 'rgba(255,255,255,0.35)', letterSpacing: '0.1em' }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {jobs.map(job => (
                  <tr
                    key={job.id}
                    style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}
                    className="hover:bg-white/[0.02] transition-colors"
                    data-testid={`job-row-${job.id}`}
                  >
                    <td className="px-5 py-4 text-white font-medium text-sm">{job.title}</td>
                    <td className="px-5 py-4">
                      <Badge variant="outline" className="text-white/60 border-white/15 text-xs">
                        {job.department}
                      </Badge>
                    </td>
                    <td className="px-5 py-4 text-white/50 text-sm">{job.location}</td>
                    <td className="px-5 py-4 text-white/50 text-sm">{job.employment_type}</td>
                    <td className="px-5 py-4 text-white/40 text-sm italic">{job.created_by_name || '\u2014'}</td>
                    <td className="px-5 py-4">
                      <Switch
                        checked={job.is_active}
                        onCheckedChange={() => handleToggle(job.id)}
                        data-testid={`toggle-${job.id}`}
                      />
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEdit(job)}
                          className="text-white/40 hover:text-white hover:bg-white/5 h-8 w-8 p-0"
                          data-testid={`edit-${job.id}`}
                        >
                          <Pencil size={14} />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(job.id)}
                          className="text-red-400/60 hover:text-red-400 hover:bg-red-500/10 h-8 w-8 p-0"
                          data-testid={`delete-${job.id}`}
                        >
                          <Trash2 size={14} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* Create / Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent
          className="sm:max-w-xl"
          style={{
            background: '#111',
            border: '1px solid rgba(255,255,255,0.1)',
            color: '#fff',
            fontFamily: "'Space Grotesk', sans-serif",
          }}
          data-testid="job-dialog"
        >
          <DialogHeader>
            <DialogTitle className="text-white text-lg" data-testid="dialog-title">
              {editingJob ? 'Edit Job Posting' : 'New Job Posting'}
            </DialogTitle>
            <DialogDescription className="text-white/40 text-sm">
              {editingJob ? 'Update the details of this job posting.' : 'Fill in the details for the new position.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2 max-h-[60vh] overflow-y-auto pr-2">
            <div>
              <Label className="text-white/50 text-xs uppercase block mb-1.5" style={{ letterSpacing: '0.1em' }}>
                Title
              </Label>
              <Input
                value={form.title}
                onChange={e => updateForm('title', e.target.value)}
                placeholder="e.g. Full-Stack Developer"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="form-title"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/50 text-xs uppercase block mb-1.5" style={{ letterSpacing: '0.1em' }}>
                  Department
                </Label>
                <select
                  value={form.department}
                  onChange={e => updateForm('department', e.target.value)}
                  style={selectStyle}
                  data-testid="form-department"
                >
                  {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
              <div>
                <Label className="text-white/50 text-xs uppercase block mb-1.5" style={{ letterSpacing: '0.1em' }}>
                  Employment Type
                </Label>
                <select
                  value={form.employment_type}
                  onChange={e => updateForm('employment_type', e.target.value)}
                  style={selectStyle}
                  data-testid="form-type"
                >
                  {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                </select>
              </div>
            </div>

            <div>
              <Label className="text-white/50 text-xs uppercase block mb-1.5" style={{ letterSpacing: '0.1em' }}>
                Location
              </Label>
              <Input
                value={form.location}
                onChange={e => updateForm('location', e.target.value)}
                placeholder="e.g. Remote, London, UK"
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="form-location"
              />
            </div>

            <div>
              <Label className="text-white/50 text-xs uppercase block mb-1.5" style={{ letterSpacing: '0.1em' }}>
                Short Description
              </Label>
              <textarea
                value={form.description}
                onChange={e => updateForm('description', e.target.value)}
                placeholder="Brief overview of the role (2-3 sentences)"
                rows={2}
                className="w-full rounded-md px-3 py-2 text-sm"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff',
                  fontFamily: "'Space Grotesk', sans-serif",
                  resize: 'vertical',
                  outline: 'none',
                }}
                data-testid="form-description"
              />
            </div>

            <div>
              <Label className="text-white/50 text-xs uppercase block mb-1.5" style={{ letterSpacing: '0.1em' }}>
                What You'll Do
              </Label>
              <textarea
                value={form.responsibilities}
                onChange={e => updateForm('responsibilities', e.target.value)}
                placeholder="One responsibility per line&#10;e.g.&#10;Lead product launches across multiple channels&#10;Manage cross-functional teams of 5+"
                rows={5}
                className="w-full rounded-md px-3 py-2 text-sm"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff',
                  fontFamily: "'Space Grotesk', sans-serif",
                  resize: 'vertical',
                  outline: 'none',
                }}
                data-testid="form-responsibilities"
              />
              <p className="text-white/25 text-xs mt-1">One item per line — each line becomes a bullet point</p>
            </div>

            <div>
              <Label className="text-white/50 text-xs uppercase block mb-1.5" style={{ letterSpacing: '0.1em' }}>
                What We're Looking For
              </Label>
              <textarea
                value={form.requirements}
                onChange={e => updateForm('requirements', e.target.value)}
                placeholder="One requirement per line&#10;e.g.&#10;5+ years in e-commerce brand management&#10;Experience with Amazon, Shopify, or similar"
                rows={5}
                className="w-full rounded-md px-3 py-2 text-sm"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: '#fff',
                  fontFamily: "'Space Grotesk', sans-serif",
                  resize: 'vertical',
                  outline: 'none',
                }}
                data-testid="form-requirements"
              />
              <p className="text-white/25 text-xs mt-1">One item per line — each line becomes a bullet point</p>
            </div>

            <div>
              <Label className="text-white/50 text-xs uppercase block mb-1.5" style={{ letterSpacing: '0.1em' }}>
                Application URL
              </Label>
              <Input
                value={form.apply_url}
                onChange={e => updateForm('apply_url', e.target.value)}
                placeholder="https://share.clickup.com/..."
                className="bg-white/5 border-white/10 text-white placeholder:text-white/30"
                data-testid="form-apply-url"
              />
              <p className="text-white/25 text-xs mt-1">ClickUp form or doc link where candidates apply</p>
            </div>

            <div className="flex items-center gap-3">
              <Switch
                checked={form.is_active}
                onCheckedChange={val => updateForm('is_active', val)}
                data-testid="form-active-toggle"
              />
              <Label className="text-white/70 text-sm">Active (visible on careers page)</Label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setShowDialog(false)}
              className="text-white/50 hover:text-white hover:bg-white/5"
              data-testid="dialog-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !form.title || !form.location || !form.description}
              className="bg-white text-black hover:bg-white/90 font-semibold"
              data-testid="dialog-save"
            >
              {saving ? 'Saving...' : editingJob ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminDashboard;
